/**
 * Utility functions for handling API errors
 */

import {
    SyncError,
    SyncErrorCode,
    SyncErrorResponse,
    getDefaultSuggestedAction
} from '../types/sync-error.types';

/**
 * Extended error information extracted from API responses
 */
export interface ExtractedError {
    message: string;
    syncError?: SyncError;
    httpStatus?: number;
}

/**
 * Extracts error message from various types of API error responses
 * Handles both JSON responses and blob responses that contain JSON error data
 *
 * @param error - The error object from a catch block
 * @returns Promise<string> - The extracted error message
 *
 * @example
 * ```typescript
 * try {
 *   await apiCall();
 * } catch (error) {
 *   const message = await extractErrorMessage(error);
 *   console.log(message); // "Worker has no recruiter"
 * }
 * ```
 */
export async function extractErrorMessage(error: unknown): Promise<string> {
    const extracted = await extractError(error);
    return extracted.message;
}

/**
 * Extracts full error information including structured sync errors
 *
 * @param error - The error object from a catch block
 * @returns Promise<ExtractedError> - The extracted error details
 */
export async function extractError(error: unknown): Promise<ExtractedError> {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown; status?: number } };
        const httpStatus = axiosError.response?.status;

        if (axiosError.response?.data) {
            // If the response is a blob but contains JSON error, we need to read it
            if (axiosError.response.data instanceof Blob) {
                try {
                    const text = await axiosError.response.data.text();
                    const errorData = JSON.parse(text) as SyncErrorResponse;
                    return {
                        message: errorData.message || 'An error occurred',
                        syncError: errorData.syncError,
                        httpStatus,
                    };
                } catch {
                    // If we can't parse as JSON, it might be a real blob error
                    return { message: 'An error occurred', httpStatus };
                }
            } else if (typeof axiosError.response.data === 'object' && axiosError.response.data !== null) {
                // If it's already JSON - check for structured sync error
                const errorData = axiosError.response.data as SyncErrorResponse;
                return {
                    message: errorData.message || 'An error occurred',
                    syncError: errorData.syncError,
                    httpStatus,
                };
            }
        }

        return { message: 'An error occurred', httpStatus };
    }

    // Fallback for other error types
    if (error instanceof Error) {
        return { message: error.message };
    }

    return { message: 'An error occurred' };
}

/**
 * Creates a SyncError from HTTP status code for client-side error categorization
 */
export function createClientSyncError(
    httpStatus: number | undefined,
    message: string,
    details?: Record<string, unknown>
): SyncError {
    let code: SyncErrorCode;

    if (!httpStatus || httpStatus === 0) {
        code = SyncErrorCode.CONNECTION_FAILED;
    } else if (httpStatus === 401) {
        code = SyncErrorCode.AUTHENTICATION_FAILED;
    } else if (httpStatus === 403) {
        code = SyncErrorCode.AUTHORIZATION_FAILED;
    } else if (httpStatus === 404) {
        code = SyncErrorCode.EASYFLEX_NOT_FOUND;
    } else if (httpStatus === 409) {
        code = SyncErrorCode.CONFLICT_DETECTED;
    } else if (httpStatus === 422) {
        code = SyncErrorCode.VALIDATION_ERROR;
    } else if (httpStatus === 429) {
        code = SyncErrorCode.RATE_LIMITED;
    } else if (httpStatus >= 500 && httpStatus < 600) {
        code = SyncErrorCode.SERVICE_UNAVAILABLE;
    } else {
        code = SyncErrorCode.UNKNOWN;
    }

    const retryable = [
        SyncErrorCode.CONNECTION_FAILED,
        SyncErrorCode.TIMEOUT,
        SyncErrorCode.SERVICE_UNAVAILABLE,
        SyncErrorCode.RATE_LIMITED,
        SyncErrorCode.STALE_DATA,
    ].includes(code);

    return {
        code,
        message,
        details,
        timestamp: new Date(),
        retryable,
        suggestedAction: getDefaultSuggestedAction(code),
        httpStatus,
    };
}

/**
 * Extracts error message synchronously (for non-blob responses)
 *
 * @param error - The error object from a catch block
 * @returns string - The extracted error message
 *
 * @example
 * ```typescript
 * try {
 *   apiCall();
 * } catch (error) {
 *   const message = extractErrorMessageSync(error);
 *   console.log(message); // "Worker has no recruiter"
 * }
 * ```
 */
export function extractErrorMessageSync(error: unknown): string {
    const extracted = extractErrorSync(error);
    return extracted.message;
}

/**
 * Extracts full error information synchronously (for non-blob responses)
 *
 * @param error - The error object from a catch block
 * @returns ExtractedError - The extracted error details
 */
export function extractErrorSync(error: unknown): ExtractedError {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown; status?: number } };
        const httpStatus = axiosError.response?.status;

        if (axiosError.response?.data && typeof axiosError.response.data === 'object' && axiosError.response.data !== null) {
            const errorData = axiosError.response.data as SyncErrorResponse;
            return {
                message: errorData.message || 'An error occurred',
                syncError: errorData.syncError,
                httpStatus,
            };
        }

        return { message: 'An error occurred', httpStatus };
    }

    if (error instanceof Error) {
        return { message: error.message };
    }

    return { message: 'An error occurred' };
} 